/**
 * Self-contained wrong-value hint demo fixture for quick Frontend testing.
 *
 * This file intentionally imports nothing. Copy/paste it into a Frontend repo,
 * stub getHint() to return one of the exported hints, and load the matching
 * puzzle state below.
 */

export type SudokuValue = number;

export type CellLocation = {
  r: number;
  c: number;
};

export type CellWithValue = {
  type: "given" | "value";
  value: SudokuValue;
};

export type CellWithNotes = {
  type: "note";
  notes: SudokuValue[];
};

export type CellProps = CellWithValue | CellWithNotes;
export type ValueCellWithLocation = CellWithValue & CellLocation;
export type NoteCellWithLocation = CellWithNotes & CellLocation;

export type HighlightType = "removal" | "placement" | "focus" | "basis";

export type HighlightedCell = {
  location: CellLocation;
  highlightType: HighlightType;
};

export type HighlightedValue = {
  location: CellLocation;
  highlightType: HighlightType;
};

export type HighlightedNote = {
  location: CellLocation;
  value: SudokuValue;
  highlightType: HighlightType;
};

export type HintStage = {
  removeValues?: ValueCellWithLocation[];
  removeNotes?: NoteCellWithLocation[];
  placeValues?: ValueCellWithLocation[];
  placeNotes?: NoteCellWithLocation[];
  highlightCells?: HighlightedCell[];
  highlightValues?: HighlightedValue[];
  highlightNotes?: HighlightedNote[];
  text?: string;
};

export type WrongValueHint = {
  strategy: "WRONG_VALUE";
  stages: HintStage[];
};

export type WrongValueDemoCase = {
  id: "direct-row-conflict" | "no-direct-conflict";
  label: string;
  puzzle: CellProps[][];
  solution: SudokuValue[][];
  hint: WrongValueHint;
};

const BASE_PUZZLE_NUMBERS: SudokuValue[][] = [
  [3, 1, 0, 0, 8, 4, 0, 0, 2],
  [2, 0, 0, 1, 5, 0, 0, 0, 6],
  [5, 7, 0, 0, 0, 3, 0, 1, 0],
  [4, 2, 3, 7, 0, 8, 0, 9, 5],
  [7, 6, 0, 0, 3, 0, 0, 0, 0],
  [0, 0, 9, 5, 6, 2, 0, 3, 0],
  [0, 5, 0, 0, 0, 6, 0, 7, 0],
  [0, 0, 7, 0, 0, 0, 9, 0, 0],
  [0, 0, 0, 0, 0, 1, 5, 0, 0],
];

const PUZZLE_SOLUTION_NUMBERS: SudokuValue[][] = [
  [3, 1, 6, 9, 8, 4, 7, 5, 2],
  [2, 9, 8, 1, 5, 7, 3, 4, 6],
  [5, 7, 4, 6, 2, 3, 8, 1, 9],
  [4, 2, 3, 7, 1, 8, 6, 9, 5],
  [7, 6, 5, 4, 3, 9, 1, 2, 8],
  [1, 8, 9, 5, 6, 2, 4, 3, 7],
  [8, 5, 1, 3, 9, 6, 2, 7, 4],
  [6, 3, 7, 2, 4, 5, 9, 8, 1],
  [9, 4, 2, 8, 7, 1, 5, 6, 3],
];

const directConflictWrongValue: ValueCellWithLocation = {
  r: 0,
  c: 3,
  type: "value",
  value: 8,
};

const conflictingGiven: ValueCellWithLocation = {
  r: 0,
  c: 4,
  type: "given",
  value: 8,
};

const noDirectConflictWrongValue: ValueCellWithLocation = {
  r: 1,
  c: 1,
  type: "value",
  value: 4,
};

const directConflictRowFocusCells: CellLocation[] = [
  { r: 0, c: 0 },
  { r: 0, c: 1 },
  { r: 0, c: 2 },
  { r: 0, c: 5 },
  { r: 0, c: 6 },
  { r: 0, c: 7 },
  { r: 0, c: 8 },
];

const directConflictFullRowFocusCells: CellLocation[] = [
  { r: 0, c: 0 },
  { r: 0, c: 1 },
  { r: 0, c: 2 },
  { r: 0, c: 3 },
  { r: 0, c: 4 },
  { r: 0, c: 5 },
  { r: 0, c: 6 },
  { r: 0, c: 7 },
  { r: 0, c: 8 },
];

function numbersToPuzzle(numbers: SudokuValue[][]): CellProps[][] {
  return numbers.map((row) =>
    row.map((value): CellProps => {
      if (value === 0) {
        return { type: "note", notes: [] };
      }

      return { type: "given", value };
    }),
  );
}

function withWrongValue(
  numbers: SudokuValue[][],
  wrongValue: ValueCellWithLocation,
): CellProps[][] {
  return numbers.map((row, r) =>
    row.map((value, c): CellProps => {
      if (r === wrongValue.r && c === wrongValue.c) {
        return {
          type: "value",
          value: wrongValue.value,
        };
      }

      if (value === 0) {
        return { type: "note", notes: [] };
      }

      return { type: "given", value };
    }),
  );
}

export const wrongValueBasePuzzle: CellProps[][] =
  numbersToPuzzle(BASE_PUZZLE_NUMBERS);

export const wrongValuePuzzleSolution: SudokuValue[][] =
  PUZZLE_SOLUTION_NUMBERS;

export const directConflictWrongValuePuzzle: CellProps[][] = withWrongValue(
  BASE_PUZZLE_NUMBERS,
  directConflictWrongValue,
);

export const noDirectConflictWrongValuePuzzle: CellProps[][] = withWrongValue(
  BASE_PUZZLE_NUMBERS,
  noDirectConflictWrongValue,
);

export const directConflictWrongValueHint: WrongValueHint = {
  strategy: "WRONG_VALUE",
  stages: [
    {
      highlightCells: [
        ...directConflictRowFocusCells.map((location) => ({
          location,
          highlightType: "focus" as const,
        })),
        { location: directConflictWrongValue, highlightType: "removal" },
        { location: conflictingGiven, highlightType: "basis" },
      ],
      text: "The 8 in row 1, column 4 conflicts with the existing 8 in the same row.",
    },
    {
      removeValues: [directConflictWrongValue],
      highlightCells: [
        ...directConflictFullRowFocusCells.map((location) => ({
          location,
          highlightType: "focus" as const,
        })),
      ],
      text: "Remove the 8 in row 1, column 4.",
    },
  ],
};

export const noDirectConflictWrongValueHint: WrongValueHint = {
  strategy: "WRONG_VALUE",
  stages: [
    {
      highlightCells: [
        { location: noDirectConflictWrongValue, highlightType: "removal" },
      ],
      text: "The 4 in row 2, column 2 is the wrong value for this cell.",
    },
    {
      removeValues: [noDirectConflictWrongValue],
      highlightCells: [
        { location: noDirectConflictWrongValue, highlightType: "focus" },
      ],
      text: "Remove the 4 in row 2, column 2.",
    },
  ],
};

export const wrongValueDemoCases: WrongValueDemoCase[] = [
  {
    id: "direct-row-conflict",
    label: "Wrong value with direct row conflict",
    puzzle: directConflictWrongValuePuzzle,
    solution: wrongValuePuzzleSolution,
    hint: directConflictWrongValueHint,
  },
  {
    id: "no-direct-conflict",
    label: "Wrong value with no direct conflict",
    puzzle: noDirectConflictWrongValuePuzzle,
    solution: wrongValuePuzzleSolution,
    hint: noDirectConflictWrongValueHint,
  },
];

export function getWrongValueDemoCase(
  id: WrongValueDemoCase["id"],
): WrongValueDemoCase {
  const demoCase = wrongValueDemoCases.find((candidate) => candidate.id === id);

  if (!demoCase) {
    throw new Error(`Unknown wrong value demo case: ${id}`);
  }

  return demoCase;
}
